import React from 'react';
import PropTypes from 'prop-types';
import { Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';

/**
 * NeoTabs
 * Barra de abas unificada do NeoApp. Encapsula o visual outonado
 * com indicador inferior animado, Outfit bold e respiros padronizados.
 */
const NeoTabs = ({
    tabs = [],
    activeTab,
    onChangeTab,
    variant = 'light', // 'light' | 'header'
    className = '',
    style = {},
    ...props
}) => {
    return (
        <React.Fragment>
            <Nav className={`status-tabs-nav neo-tabs-${variant} ${className}`} style={{ ...style }} {...props}>
                {tabs.map((tab) => (
                    <NavItem key={tab.id}>
                        <NavLink
                            className={classnames('status-tab-link', { active: activeTab === tab.id })}
                            onClick={() => onChangeTab(tab.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            {tab.icon && <i className={`mdi ${tab.icon} me-1.5 font-size-14`}></i>}
                            <span>{tab.label}</span>
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            <style>{`
                /* Variante Light (Padrão) */
                .status-tabs-nav.neo-tabs-light {
                    display: flex !important;
                    gap: 24px !important;
                    padding: 0 !important;
                    border-bottom: 2px solid rgba(72, 39, 18, 0.12) !important;
                    margin-bottom: 24px !important;
                }
                .status-tabs-nav.neo-tabs-light .nav-link.status-tab-link {
                    font-family: 'Outfit', sans-serif !important;
                    font-weight: 800 !important;
                    text-transform: uppercase !important;
                    font-size: 11.5px !important;
                    letter-spacing: 0.8px !important;
                    border: none !important;
                    background: transparent !important;
                    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    cursor: pointer !important;
                    border-radius: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    white-space: nowrap !important;
                    padding: 10px 4px !important;
                    color: var(--color-brand-primary) !important;
                    opacity: 0.65 !important;
                    border-bottom: 3px solid transparent !important;
                }
                .status-tabs-nav.neo-tabs-light .nav-link.status-tab-link:hover {
                    opacity: 0.9 !important;
                    color: var(--color-brand-accent) !important;
                }
                .status-tabs-nav.neo-tabs-light .nav-link.status-tab-link.active {
                    opacity: 1 !important;
                    color: var(--color-brand-accent) !important;
                    border-bottom: 3px solid var(--color-brand-accent) !important;
                }

                /* Variante Header (Perfil / Fundo Escuro) */
                .status-tabs-nav.neo-tabs-header {
                    display: flex !important;
                    margin-bottom: 0 !important;
                    padding-bottom: 0 !important;
                    scrollbar-width: none !important;
                    background-color: var(--color-brand-primary) !important;
                    border-radius: 16px 16px 0 0 !important;
                    padding: 4px 12px 0 12px !important;
                    border: 1.5px solid rgba(254, 245, 239, 0.15) !important;
                    border-bottom: none !important;
                    box-shadow: 0 -4px 12px rgba(72, 39, 18, 0.06) !important;
                    gap: 4px !important;
                    overflow: auto !important;
                    flex-wrap: nowrap !important;
                }
                .status-tabs-nav.neo-tabs-header::-webkit-scrollbar {
                    display: none !important;
                }
                .status-tabs-nav.neo-tabs-header .nav-link.status-tab-link {
                    font-family: 'Outfit', sans-serif !important;
                    border: none !important;
                    background: transparent !important;
                    cursor: pointer !important;
                    border-radius: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    white-space: nowrap !important;
                    padding: 12px 18px !important;
                    font-size: 13.5px !important;
                    text-transform: none !important;
                    letter-spacing: normal !important;
                    font-weight: 600 !important;
                    color: rgba(255, 255, 255, 0.7) !important;
                    border-bottom: 3px solid transparent !important;
                    transition: all 0.2s ease !important;
                }
                .status-tabs-nav.neo-tabs-header .nav-link.status-tab-link:hover {
                    color: #ffffff !important;
                }
                .status-tabs-nav.neo-tabs-header .nav-link.status-tab-link.active {
                    color: #ffffff !important;
                    border-bottom: 3px solid var(--color-brand-accent) !important;
                    font-weight: 800 !important;
                }
                .status-tabs-nav.neo-tabs-header .nav-link.status-tab-link i {
                    color: rgba(255, 255, 255, 0.4) !important;
                    font-size: 17px !important;
                    margin-right: 8px !important;
                    transition: all 0.2s ease !important;
                }
                .status-tabs-nav.neo-tabs-header .nav-link.status-tab-link.active i {
                    color: var(--color-brand-accent) !important;
                }
            `}</style>
        </React.Fragment>
    );
};

NeoTabs.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.string
        })
    ).isRequired,
    activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChangeTab: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['light', 'header']),
    className: PropTypes.string,
    style: PropTypes.object
};

export default NeoTabs;
